import isAutoGeneratedTeamName from './isAutoGeneratedTeamName';

interface PlayerLike {
  name?: string;
  info?: { first_name?: string; last_name?: string };
}

interface TeamLike {
  name?: string;
  teamName?: string;
  players?: PlayerLike[];
}

/**
 * Gets the display name for a team.
 * If the team has an auto-generated name (zqtm_ prefix), shows player names instead.
 * 
 * @param {Object} team - The team or divisionTeam object (must have players populated via virtual)
 * @param {Object} config - Optional configuration object
 * @param {boolean} config.forceShowPlayers - Force showing player names regardless of team name (default: false)
 * @param {number} config.displayPlayerCount - Number of players to display, max 3 (default: 2)
 * @returns {string} The display name for the team
 */
export default function getTeamDisplayName(
  team: TeamLike | null | undefined,
  config: { forceShowPlayers?: boolean; displayPlayerCount?: number } = {},
): string {
  const { forceShowPlayers = false, displayPlayerCount = 2 } = config;

  const teamName = team?.name || team?.teamName || '';

  // Check if we should show player names instead
  const shouldShowPlayers = forceShowPlayers || isAutoGeneratedTeamName(teamName);

  if (!shouldShowPlayers) {
    return teamName || 'Unknown Team';
  }

  // Get players from virtual populate
  const players = team?.players || [];
  if (players.length === 0) {
    return 'TBD';
  }

  // Cap displayPlayerCount at 3, and use available players if less than requested
  const playerCountToShow = Math.min(displayPlayerCount, 3, players.length);

  const playerNames = players.slice(0, playerCountToShow).map(p => {
    // Try to get player name from different possible locations
    if (p.name) return p.name;
    const firstName = p.info?.first_name || '';
    const lastName = p.info?.last_name || '';
    // Abbreviate last name to first letter
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() + '.' : '';
    return `${firstName} ${lastInitial}`.trim();
  }).filter(Boolean);

  return playerNames.join(' / ') || 'TBD';
}
